/**
 * Imperative Cytoscape mount (I12): React never re-renders the canvas from state. Cytoscape and its
 * layout extensions are lazy-loaded on first use (spec §8.1).
 * Verified against cytoscape 3.34 (own typings), cytoscape-fcose 2.2 (`fixedNodeConstraint`),
 * cytoscape-elk 2.3 (`{ name: "elk", elk: { algorithm } }`).
 */
import type { Core, ElementDefinition, Ext, LayoutOptions } from "cytoscape";
import type { MappedGraph } from "./conventionMapper";
import type { NodeInfo } from "./nodeInfo";
import { buildStylesheet, type StyleOverrides } from "./stylesheet";

export type LayoutName = "fcose" | "elk-layered" | "preset";
export type Positions = Record<string, { x: number; y: number }>;

export interface CyCallbacks {
  onSelect?(id: string | null): void;
  onExpand?(id: string): void;
  onDragEnd?(id: string, pos: { x: number; y: number }): void;
}

export interface RenderOptions {
  layout: LayoutName;
  positions?: Positions;
  parents?: Map<string, string>;
  overrides?: StyleOverrides;
  /** keep existing nodes in place and lay out only the new ones (expansion, spec §15.7) */
  incremental?: boolean;
  /** [M21] pan new results into view at the reader's own zoom (the overlay's toggle, §15.7) */
  autoCentre?: boolean;
}

export interface CyHandle {
  cy: Core;
  render(graph: MappedGraph, info: Map<string, NodeInfo>, opts: RenderOptions): void;
  /** [M21] step the zoom about the centre of the canvas, clamped to the renderer's bounds (§15.7) */
  zoomBy(factor: number, animate: boolean): void;
  /** [M21] frame the whole graph */
  fitView(animate: boolean): void;
  destroy(): void;
}

type CytoscapeFactory = typeof import("cytoscape");
let loader: Promise<CytoscapeFactory> | null = null;

/** ESM/CJS interop: the bundled module may or may not wrap its export in `default`. */
function interop<T>(mod: unknown): T {
  const m = mod as { default?: T };
  return (m.default ?? mod) as T;
}

function loadCytoscape(): Promise<CytoscapeFactory> {
  const existing = loader;
  if (existing) return existing;
  const created = (async () => {
    const cytoscape = interop<CytoscapeFactory>(await import("cytoscape"));
    cytoscape.use(interop<Ext>(await import("cytoscape-fcose")));
    cytoscape.use(interop<Ext>(await import("cytoscape-elk")));
    return cytoscape;
  })();
  loader = created;
  return created;
}

function elements(graph: MappedGraph, info: Map<string, NodeInfo>, parents?: Map<string, string>): ElementDefinition[] {
  const ids = new Set(graph.nodes.map((n) => n.id));
  const out: ElementDefinition[] = [];
  const parentIds = new Set(parents ? [...parents.values()] : []);
  // compound parents that are not themselves in the result still need a node (spec §15.7)
  for (const p of parentIds) {
    if (!ids.has(p)) out.push({ group: "nodes", data: { id: p, label: info.get(p)?.label ?? "page", style: "document", kind: "doc" } });
  }
  for (const n of graph.nodes) {
    const i = info.get(n.id);
    const parent = parents?.get(n.id);
    out.push({
      group: "nodes",
      data: {
        id: n.id,
        label: i?.label ?? n.id,
        style: i?.style ?? "default",
        kind: i?.kind ?? n.kind,
        ...(i?.thumb ? { thumb: i.thumb } : {}),
        ...(parent && parent !== n.id ? { parent } : {}),
      },
    });
  }
  for (const e of graph.edges) {
    const label = e.label ?? e.predicate.split(/[#/]/).pop() ?? e.predicate;
    out.push({
      group: "edges",
      data: {
        id: e.id,
        source: e.source,
        target: e.target,
        label,
        predicate: e.predicate,
        provenance: e.provenance,
        conf: e.provenance === "derived" ? (e.conf ?? 0.5) : 1,
        stale: e.stale ? 1 : 0,
      },
    });
  }
  return out;
}

function layoutOptions(name: LayoutName, fixed: Array<{ nodeId: string; position: { x: number; y: number } }>, incremental: boolean, keepViewport: boolean): LayoutOptions {
  if (name === "elk-layered") {
    return { name: "elk", animate: false, fit: !keepViewport, elk: { algorithm: "layered", "elk.direction": "RIGHT" } } as unknown as LayoutOptions;
  }
  return {
    name: "fcose",
    animate: false,
    fit: !incremental && !keepViewport,
    randomize: !incremental,
    quality: "default",
    nodeRepulsion: 6000,
    idealEdgeLength: 90,
    ...(fixed.length ? { fixedNodeConstraint: fixed } : {}),
  } as unknown as LayoutOptions;
}

export async function mountCytoscape(container: HTMLElement, cb: CyCallbacks): Promise<CyHandle> {
  const cytoscape = await loadCytoscape();
  const cy = cytoscape({ container, style: buildStylesheet(), wheelSensitivity: 0.3, minZoom: 0.1, maxZoom: 4 });
  // test/diagnostic hook without a global: Playwright reaches the instance through the DOM node
  (container as HTMLElement & { __cy?: Core }).__cy = cy;

  // [M21] the viewport belongs to the reader (spec §15.8). The canvas frames the graph on its own
  // only until they first zoom, pan or fit; after that nothing moves the view but them — not an
  // expansion, not a re-run, not a change of layout. A re-run may pan the new results into view at
  // their zoom, and only while the overlay's auto-centre toggle is on.
  let userViewport = false;
  let applying = 0;
  // the level the last button press is animating towards: clicking `+` three times quickly must
  // zoom three steps, not three times from wherever the running animation happens to be
  let pendingZoom: number | null = null;
  let animating = false;
  const applied = <T,>(fn: () => T): T => {
    applying++;
    try {
      return fn();
    } finally {
      queueMicrotask(() => applying--);
    }
  };
  cy.on("zoom pan", () => {
    if (applying) return;
    userViewport = true;
    if (!animating) pendingZoom = null; // a wheel or pinch takes over from the buttons
  });
  const centre = () => ({ x: cy.width() / 2, y: cy.height() / 2 });
  cy.on("tap", "node", (e) => cb.onSelect?.(e.target.id()));
  cy.on("tap", (e) => {
    if (e.target === cy) cb.onSelect?.(null);
  });
  cy.on("dbltap", "node", (e) => cb.onExpand?.(e.target.id()));
  cy.on("dragfree", "node", (e) => {
    const p = e.target.position();
    cb.onDragEnd?.(e.target.id(), { x: Math.round(p.x), y: Math.round(p.y) });
  });

  return {
    cy,
    zoomBy(factor, animate) {
      const level = Math.min(cy.maxZoom(), Math.max(cy.minZoom(), (pendingZoom ?? cy.zoom()) * factor));
      userViewport = true;
      pendingZoom = level;
      if (!animate) {
        cy.zoom({ level, renderedPosition: centre() });
        pendingZoom = null;
        return;
      }
      animating = true;
      cy.animate(
        { zoom: { level, renderedPosition: centre() }, duration: 120 },
        {
          // a later press replaces this animation instead of waiting behind it
          queue: false,
          complete: () => {
            animating = false;
            if (pendingZoom === level) pendingZoom = null;
          },
        },
      );
    },
    fitView(animate) {
      userViewport = true;
      pendingZoom = null;
      if (animate) {
        animating = true;
        cy.animate({ fit: { eles: cy.elements(), padding: 30 }, duration: 120 }, { queue: false, complete: () => (animating = false) });
      } else cy.fit(undefined, 30);
    },
    render(graph, info, opts) {
      const els = elements(graph, info, opts.parents);
      cy.style(buildStylesheet(opts.overrides ?? {}));
      const before = new Map<string, { x: number; y: number }>(cy.nodes().map((n) => [n.id(), { ...n.position() }]));
      const incremental = !!opts.incremental && before.size > 0;
      cy.batch(() => {
        if (!incremental) cy.elements().remove();
        const present = new Set(cy.elements().map((e) => e.id()));
        cy.add(els.filter((e) => !present.has(String(e.data.id))));
        // refresh data of existing elements (labels/styles arrive after describe-nodes)
        for (const e of els) {
          const existing = cy.getElementById(String(e.data.id));
          if (existing.nonempty()) existing.data(e.data);
        }
      });
      const fixed = incremental ? [...before.entries()].filter(([id]) => cy.getElementById(id).nonempty()).map(([nodeId, position]) => ({ nodeId, position })) : [];
      const layoutName: LayoutName = opts.layout === "preset" ? "fcose" : opts.layout;
      const keepViewport = userViewport;
      applied(() => {
        try {
          cy.layout(layoutOptions(layoutName, fixed, incremental, keepViewport)).run();
        } catch (err) {
          console.warn("layout failed, falling back to grid", err);
          cy.layout({ name: "grid" }).run();
        }
      });
      // pinned positions win (graph_view `positions`, spec §7.4.7); preset uses only these
      const pinned = opts.positions ?? {};
      for (const [id, pos] of Object.entries(pinned)) {
        const n = cy.getElementById(id);
        if (n.nonempty()) n.position(pos);
      }
      if (!keepViewport && !incremental && Object.keys(pinned).length && opts.layout === "preset") applied(() => cy.fit(undefined, 30));
      // the reader's zoom stays; only the pan follows the new results, and only if they asked for it
      if (keepViewport && !incremental && opts.autoCentre) applied(() => cy.center());
      container.dataset.nodes = String(cy.nodes().length);
      container.dataset.edges = String(cy.edges().length);
    },
    destroy() {
      cy.destroy();
    },
  };
}
